import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo, LogoMark } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { ThemeToggle, LanguageToggle } from "@/components/Toggles";
import { PhoneLoginPanel } from "@/components/PhoneLoginPanel";
import { Loader2, Smartphone, Mail } from "lucide-react";

const formatError = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join(", ");
  return JSON.stringify(d);
};

export default function Login() {
  const { login } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [method, setMethod] = useState("phone"); // "phone" | "email"

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const u = await login(email, pw);
      const target =
        u.role === "customer" ? "/app" :
        u.role === "restaurant_owner" ? "/restaurant" :
        u.role === "rider" ? "/rider" :
        u.role === "provider" ? "/provider" : "/admin";
      navigate(location.state?.from?.pathname || target, { replace: true });
    } catch (e) {
      setErr(formatError(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Left hero */}
      <div className="lg:flex-1 so-gradient-hero so-grain text-white p-8 md:p-16 flex flex-col justify-between">
        <Link to="/"><Logo size={36} /></Link>
        <div className="my-12 lg:my-0">
          <LogoMark size={72} animated />
          <h2 className="mt-6 text-3xl md:text-5xl font-bold leading-tight">Welcome back.</h2>
          <p className="mt-3 text-white/70 max-w-md">
            Sign in to your ServeOn account to manage your orders, services or business.
          </p>
        </div>
        <div className="text-xs text-white/60">© 2026 ServeOn</div>
      </div>

      {/* Right form */}
      <div className="lg:flex-1 flex flex-col">
        <div className="flex justify-end gap-2 p-4">
          <LanguageToggle /><ThemeToggle />
        </div>
        <div className="flex-1 flex items-center justify-center px-6 pb-10">
          <div className="w-full max-w-md" data-testid="login-panel">
            <h1 className="text-3xl font-bold">{t("login")}</h1>
            <p className="text-sm text-[var(--so-text-soft)] mt-1">
              {lang === "ar" ? "اختر طريقة الدخول" : "Choose how to sign in"}
            </p>

            {/* Method tabs */}
            <div className="mt-5 grid grid-cols-2 gap-2 p-1 rounded-xl bg-[var(--so-border)]/40" data-testid="login-tabs">
              <button
                type="button"
                onClick={() => setMethod("phone")}
                data-testid="tab-phone"
                className={`py-2 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-all ${method === "phone" ? "bg-white dark:bg-[var(--so-surface)] shadow text-[var(--so-orange)]" : "text-[var(--so-text-soft)]"}`}
              >
                <Smartphone className="w-4 h-4" />
                {lang === "ar" ? "الهاتف" : "Phone"}
              </button>
              <button
                type="button"
                onClick={() => setMethod("email")}
                data-testid="tab-email"
                className={`py-2 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-all ${method === "email" ? "bg-white dark:bg-[var(--so-surface)] shadow text-[var(--so-orange)]" : "text-[var(--so-text-soft)]"}`}
              >
                <Mail className="w-4 h-4" />
                {lang === "ar" ? "بريد إلكتروني" : "Email"}
              </button>
            </div>

            <div className="mt-6">
              {method === "phone" ? (
                <PhoneLoginPanel />
              ) : (
                <form onSubmit={handleSubmit} data-testid="login-form">
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-[var(--so-text-soft)]">{t("email")}</label>
                      <input
                        type="email" required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="so-input mt-1"
                        data-testid="login-email"
                        placeholder="you@example.com"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold uppercase tracking-wider text-[var(--so-text-soft)]">{t("password")}</label>
                        <Link to="/forgot-password" className="text-xs font-semibold text-[var(--so-orange)] hover:underline" data-testid="forgot-password-link">
                          {lang === "ar" ? "نسيت كلمة المرور؟" : "Forgot password?"}
                        </Link>
                      </div>
                      <input
                        type="password" required
                        value={pw}
                        onChange={(e) => setPw(e.target.value)}
                        className="so-input mt-1"
                        data-testid="login-password"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>

                  {err && <div className="mt-4 text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="login-error">{err}</div>}

                  <button type="submit" disabled={loading} className="so-btn-primary w-full justify-center mt-6" data-testid="login-submit">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("continue")}
                  </button>
                </form>
              )}
            </div>

            <div className="mt-6 text-sm text-center text-[var(--so-text-soft)]">
              New to ServeOn? <Link to="/register" className="text-[var(--so-orange)] font-semibold" data-testid="signup-link">{t("signup")}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
